import React, { useState } from "react";
import { Button, Form, Row, Col, Card } from "react-bootstrap";
import DataGrid, { Column } from "devextreme-react/data-grid";
import {
  GetCustomerByMobileNo,
  InsertCustomerReferral,
  SendOtp,
  VerifyOtp,
} from "./services/referralservices";
import notistack from "modules/core/notistack";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";

interface ReferralFormInputs {
  name: string;
  phone: string;
  email: string;
  address: string;
}

const CustomerReferral = () => {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerID, setCustomerID] = useState("");
  const [otpId, setOtpId] = useState<number | null>(null);

  const [referrals, setReferrals] = useState<any[]>([]);
  const [phoneError, setPhoneError] = useState<string | false>(false);

  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);

  // react-hook-form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReferralFormInputs>();

  const handleCheckPhone = async () => {
    if (!phone) {
      setPhoneError("Phone number is required");
      return;
    }
    try {
      const res = await GetCustomerByMobileNo(phone);
      if (res.status === 200) {
        setCustomerName(res.data.name);
        setIsPhoneValid(true);

        const otpRes = await SendOtp({
          Purpose: "Refferel",
          MobileNo: phone,
        });
        if (otpRes.status === 200) {
          notistack.notifyFromApi(otpRes);
          setOtpId(otpRes.data);
        } else {
          notistack.notifyFromApi(otpRes);
        }
      }
      if (res.status === 400) {
        setPhoneError(
          res.data.message ||
          "This number is not assigned to any customer. Please enter a valid number."
        );
        setIsPhoneValid(false);
        setCustomerName("");
      }
    } catch (error) {
      console.error("handleCheckPhone error", error);
      notistack.error("Something went wrong while verifying phone");
      setIsPhoneValid(false);
    }
  };

  const handleOtpChange = async (value: string) => {
    setOtp(value);

    if (value.length === 6) {
      try {
        const res = await VerifyOtp({
          id: otpId,
          otp: value,
        });
        if (res.status === 200) {
          notistack.notifyFromApi(res);
          setIsOtpVerified(true);

          const customerRes = await GetCustomerByMobileNo(phone);
          if (customerRes.status === 200) {
            setCustomerName(
              customerRes.data.firstName + " " + customerRes.data.lastName || ""
            );
            setCustomerID(customerRes.data.id);
          }

          setIsPhoneValid(true);
        } else {
          notistack.notifyFromApi(res);
          setIsOtpVerified(false);
        }
      } catch (error) {
        notistack.error("Error verifying OTP");
        setIsOtpVerified(false);
      }
    }
  };

  const handleAddReferral = (data: ReferralFormInputs) => {
    setReferrals([...referrals, data]);
    reset(); // reset form after adding
  };

  const handleSave = async () => {
    if (!phone || !isOtpVerified || referrals.length === 0) {
      notistack.warning("Please verify OTP and add at least one referral");
      return;
    }

    try {
      const payload = referrals.map((ref) => ({
        referrerCustomerId: customerID,
        referredCustomerName: ref.name,
        referredCustomerPhone: ref.phone,
        referredCustomerEmail: ref.email,
        referredCustomerAddress: ref.address,
      }));

      const res = await InsertCustomerReferral(payload);

      if (res.status == 200) {
        notistack.notifyFromApi(res);
        setReferrals([]);
        setOtp("");
        setIsOtpVerified(false);
        setCustomerName("");
        setPhone("");
      } else {
        notistack.notifyFromApi(res);
      }
    } catch (err) {
      console.error(err);
      notistack.error("Error saving referrals");
    }
  };

  const handleDeleteReferral = (phoneToDelete: string) => {
    setReferrals(referrals.filter((ref) => ref.phone !== phoneToDelete));
  };

  return (
    <Card className="p-3">
      <h4 className="text-center mb-4">Customer Referral</h4>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Label>
            Phone No <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            placeholder="Phone No"
            maxLength={10}
            value={phone}
            disabled={isOtpVerified}
            onChange={(e) => {
              const onlyNums = e.target.value.replace(/\D/g, "");
              setPhone(onlyNums.slice(0, 10));
              setPhoneError(false);
            }}
            onKeyDown={(e) => e.key === "Enter" && handleCheckPhone()}
          />
          {phoneError && <small style={{ color: "red" }}>{phoneError}</small>}
        </Col>
        <Col md={3}>
          <Form.Label>
            OTP <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            placeholder="OTP"
            maxLength={6}
            value={otp}
            disabled={!isPhoneValid || isOtpVerified}
            onChange={(e) => handleOtpChange(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Label>Customer Name</Form.Label>
          <Form.Control
            placeholder="Customer Name"
            value={customerName}
            readOnly
            disabled={!isPhoneValid}
          />
        </Col>
      </Row>

      <h5 className="mb-3">Add Referral</h5>
      <Form onSubmit={handleSubmit(handleAddReferral)}>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Label>
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              placeholder="Customer Name"
              disabled={!isOtpVerified}
              {...register("name", { required: "Name is required", maxLength: { value: 50, message: "Max 50 characters allowed" } })}
            />
            {errors.name && (
              <small style={{ color: "red" }}>{errors.name.message}</small>
            )}
          </Col>
          <Col md={3}>
            <Form.Label>
              Phone <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              placeholder="Customer Phone"
              maxLength={10}
              disabled={!isOtpVerified}
              {...register("phone", {
                required: "Phone is required",
                pattern: {
                  value: /^[0-9]{10}$/,
                  message: "Phone must be 10 digits",
                },
                validate: {
                  notAllSame: (val) =>
                    val && /^(\d)\1{9}$/.test(val)
                      ? "Phone number cannot have all digits the same"
                      : true,
                },
              })}
            />
            {errors.phone && (
              <small style={{ color: "red" }}>{errors.phone.message}</small>
            )}
          </Col>
          <Col md={3}>
            <Form.Label>Email</Form.Label>
            <Form.Control
              placeholder="Customer Email"
              disabled={!isOtpVerified}
              {...register("email", {
                maxLength: {
                  value: 100,
                  message: "Email cannot exceed 100 characters",
                },
                pattern: {
                  value:
                    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Invalid email format",
                },
              })}
            />
            {errors.email && (
              <small style={{ color: "red" }}>{errors.email.message}</small>
            )}
          </Col>
          <Col md={2}>
            <Form.Label>Address</Form.Label>
            <Form.Control
              placeholder="Address"
              disabled={!isOtpVerified}
              {...register("address", {
                maxLength: {
                  value: 200,
                  message: "Address cannot exceed 200 characters",
                },
              })}
            />
            {errors.address && (
              <small style={{ color: "red" }}>{errors.address.message}</small>
            )}
          </Col>
          <Col md={1} className="d-flex align-items-end">
            <Button
              className="btn submit-btn"
              type="submit"
              disabled={!isOtpVerified}
            >
              Add
            </Button>
          </Col>
        </Row>
      </Form>

      <DataGrid dataSource={referrals} keyExpr="phone" className="mb-3">
        <Column
          caption="Action"
          width={100}
          cellRender={({ data }) => (
            <button
              className="btn btn-sm action-btn delete-btn"
              onClick={() => handleDeleteReferral(data.phone)}
            >
              <FontAwesomeIcon icon={faTrash} className="ml-2" />
            </button>
          )}
        />
        <Column dataField="name" caption="Customer Name" />
        <Column dataField="phone" caption="Customer Phone" />
        <Column dataField="email" caption="Customer Email" />
        <Column dataField="address" caption="Address" />
      </DataGrid>

      <div className="text-end">
        <Button
          className="btn submit-btn"
          onClick={handleSave}
          disabled={!isOtpVerified || referrals.length === 0}
        >
          Save
        </Button>
      </div>
    </Card>
  );
};

export default CustomerReferral;
